import useMediaQuery from '@/core/listeners/useMediaQuery';
import { useCallback, useMemo, useState } from 'react';

export type HomeImageVariant = 'default' | 'flipped';

const makeResponsiveSources = (index: number, variant: HomeImageVariant) => {
  /* The flipped set is versioned so a rebuilt hero is actually re-fetched:
   * the filename is otherwise unchanged and browsers keep the old bytes. */
  const folder =
    variant === 'flipped' ? `${index + 1}-flipped-v2` : `${index + 1}`;
  return {
    mobile: {
      before: `/images/home/${folder}/mobile/before.webp`,
      after: `/images/home/${folder}/mobile/after.webp`,
    },
    desktop: {
      before: `/images/home/${folder}/before.webp`,
      after: `/images/home/${folder}/after.webp`,
    },
  };
};

enum WindowSize {
  SMALL,
  MEDIUM,
  LARGE,
}
const getComparerConfig = (
  size: WindowSize,
  homepageImage: number,
  variant: HomeImageVariant
) => {
  const responsiveSources = makeResponsiveSources(homepageImage, variant);
  if (size === WindowSize.SMALL) {
    return {
      beforeSrc: responsiveSources.mobile.before,
      afterSrc: responsiveSources.mobile.after,
      initialSliderProgress: 0.5,
      maxSliderProgress: 1,
    };
  }
  if (size === WindowSize.MEDIUM) {
    return {
      beforeSrc: responsiveSources.desktop.before,
      afterSrc: responsiveSources.desktop.after,
      initialSliderProgress: 0.325,
      maxSliderProgress: 0.4,
    };
  }
  return {
    beforeSrc: responsiveSources.desktop.before,
    afterSrc: responsiveSources.desktop.after,
    initialSliderProgress: 0.325,
    maxSliderProgress: 0.5,
  };
};

const emptyComparerConfig = {
  beforeSrc: '',
  afterSrc: '',
  initialSliderProgress: 0,
  maxSliderProgress: 0,
};

export const useResponsiveComparer = (
  imageVariant: HomeImageVariant = 'default'
) => {
  const homepageImage = 1;
  const [windowSize, setWindowSize] = useState<WindowSize>();

  const applyWindowSize = useCallback(
    (size: WindowSize) => (doesMatch: boolean) => {
      if (doesMatch) setWindowSize(size);
    },
    []
  );

  const comparerConfigCb = useMemo(
    () => ({
      small: applyWindowSize(WindowSize.SMALL),
      medium: applyWindowSize(WindowSize.MEDIUM),
      large: applyWindowSize(WindowSize.LARGE),
    }),
    [applyWindowSize]
  );

  useMediaQuery('(max-width: 1024px)', comparerConfigCb.small);
  useMediaQuery(
    '(min-width: 1024px) and (max-width: 1300px)',
    comparerConfigCb.medium
  );
  useMediaQuery('(min-width: 1300px)', comparerConfigCb.large);

  /* Derive during render so a variant flip (control -> left_centered_flipped)
   * swaps the hero set in the same paint. */
  return useMemo(
    () =>
      windowSize === undefined
        ? emptyComparerConfig
        : getComparerConfig(windowSize, homepageImage, imageVariant),
    [windowSize, homepageImage, imageVariant]
  );
};
